import type { BenchmarkWindowDays } from '../../models/benchmark.types'

const ALLOWED_WINDOWS = new Set<BenchmarkWindowDays>([7, 14, 30, 60])
const DAY_MS = 86_400_000

export interface BenchmarkDateRequest {
  compareTimeWindow: BenchmarkWindowDays
  postData: string
}

function parseApiDate(value: string | null): Date | null {
  if (!value) return null
  const date = new Date(`${value}T00:00:00.000Z`)
  return Number.isNaN(date.getTime()) ? null : date
}

function apiDate(value: Date): string {
  return value.toISOString().slice(0, 10)
}

/**
 * Add the hidden, immediately-preceding equal-length comparison period to the shared picker query.
 * The benchmark picker itself is restricted to these four windows, but malformed input still
 * fails safely to the legacy 30-day default.
 */
export function buildBenchmarkDateRequest(defaultPostData: string): BenchmarkDateRequest {
  const params = new URLSearchParams(defaultPostData.replace(/^&/u, ''))
  const start = parseApiDate(params.get('date_start'))
  const end = parseApiDate(params.get('date_end'))
  const inclusiveDays =
    start && end ? Math.floor((end.getTime() - start.getTime()) / DAY_MS) + 1 : 30
  const compareTimeWindow = ALLOWED_WINDOWS.has(inclusiveDays as BenchmarkWindowDays)
    ? (inclusiveDays as BenchmarkWindowDays)
    : 30

  if (start && end) {
    const compareEnd = new Date(start.getTime() - DAY_MS)
    const compareStart = new Date(compareEnd.getTime() - (compareTimeWindow - 1) * DAY_MS)
    params.set('compare_type', 'prev')
    params.set('compare_date_start', apiDate(compareStart))
    params.set('compare_date_end', apiDate(compareEnd))
  }

  return { compareTimeWindow, postData: `&${params.toString()}` }
}
